/**
 * DUAL-FIELD AUTOCOMPLETE — Combobox MUI freeSolo per lookup con dual-field.
 *
 * Pattern: l'utente sceglie un'opzione dall'autocomplete (id valorizzato)
 * oppure digita testo libero (id=0, name=testo). Il consumer manda al
 * backend solo uno dei due campi — tipicamente il backend fa find-or-create
 * sul nome quando arriva senza id (vedi PersonSource → personSource: string).
 *
 * Use case attuali:
 *   - PersonFormBody (drawer create/edit Contatto) — campo `Fonte`
 *   - EditableField variant `lookupFreeSolo` (inline-edit dettaglio entità)
 *
 * Pattern tecnico interno:
 *   - useAsyncAutocomplete "decapitato" (solo `options` + `setQuery`):
 *     l'`inputValue` è driven dal `value.name` esterno, non dal query
 *     interno del hook. Spread-are `inputValue`/`onInputChange` dell'hook
 *     genererebbe casi in cui il textbox mostra un valore diverso da
 *     quello che verrà committato (vedi LESSONS_LEARNED §Hook decapitato).
 *   - `onInputChange` ignora il reason `'reset'` (sincronia interna MUI
 *     post-fetch che rimpiazzerebbe il testo con getOptionLabel(value)).
 *   - Ogni keystroke azzera `id`: la selezione esplicita riavviene solo
 *     via reason `'selectOption'` su un option matchato dal dropdown.
 *
 * NB: il fetcher deve avere identity stabile (useCallback nel chiamante),
 * altrimenti il useEffect di useAsyncAutocomplete refetcha continuamente.
 */

import { useEffect, type RefObject } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useAsyncAutocomplete } from '@crm/shared';
import type { LookupOption } from './EditableField';

/**
 * Shape del valore dual-field. `id === 0` significa "nessuna selezione,
 * solo testo libero". `name` è SEMPRE il testo visualizzato — quando id>0
 * deve coincidere con la label dell'option.
 */
export interface DualFieldValue {
  id: number;
  name: string;
}

interface DualFieldAutocompleteProps {
  value: DualFieldValue;
  onChange: (next: DualFieldValue) => void;
  /** Identity stabile (useCallback nel chiamante). */
  fetcher: (query: string) => Promise<LookupOption[]>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  className?: string;
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
  inputRef?: RefObject<HTMLInputElement | null>;
}

export function DualFieldAutocomplete({
  value,
  onChange,
  fetcher,
  label,
  placeholder,
  required,
  disabled,
  size,
  fullWidth,
  className,
  error,
  helperText,
  onBlur,
  inputRef,
}: DualFieldAutocompleteProps) {
  // Safety net: se il chiamante passa value.name/value.id come null
  // (es. record DB pre-introduzione del campo), MUI Autocomplete fa boom
  // su `inputValue.length`. Normalizzo qui al boundary del component.
  const name = value.name ?? '';
  const id = value.id ?? 0;

  const { options, setQuery } = useAsyncAutocomplete<LookupOption>({
    fetcher,
    initialQuery: name,
  });

  // Allinea la query del fetcher al testo del value (sia per typing
  // dell'utente sia per cambi esterni del value via selectOption).
  useEffect(() => {
    setQuery(name);
  }, [name, setQuery]);

  const selectedOption =
    id > 0 ? (options.find((o) => o.id === id) ?? null) : null;

  return (
    <Autocomplete<LookupOption, false, false, true>
      freeSolo
      size={size}
      fullWidth={fullWidth}
      className={className}
      options={options}
      filterOptions={(x) => x}
      inputValue={name}
      onInputChange={(_event, newValue, reason) => {
        if (reason === 'reset') return;
        onChange({ id: 0, name: newValue });
      }}
      value={selectedOption}
      onChange={(_event, newVal) => {
        if (newVal == null) {
          onChange({ id: 0, name: '' });
        } else if (typeof newVal === 'string') {
          // freeSolo: createOption (Enter su testo libero non matchato).
          onChange({ id: 0, name: newVal });
        } else {
          // `LookupOption.id` è `number | string` da TS-114 (i codici articolo del gestionale
          // sono stringhe), ma il dual-field è numerico per costruzione: l'id serve al
          // find-or-create lato server, e `0` significa «solo testo libero». Una chiave non
          // numerica qui non ha senso, e `Number(...)` la riporta a `0` — cioè al caso del
          // testo libero, che è la lettura giusta.
          onChange({ id: Number(newVal.id) || 0, name: newVal.label });
        }
      }}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
      isOptionEqualToValue={(a, b) =>
        typeof a !== 'string' && typeof b !== 'string' && a.id === b.id
      }
      disabled={disabled}
      onBlur={onBlur}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
}
